import React, {createContext, ReactNode, useContext, useState, useCallback} from 'react';
import {PluginContext} from "./PluginType";
import {apiRequest} from "../../util/ApiUtils";
import {appConfig} from "../../config";

// Default placeholder implementation (will be replaced by Provider)
const defaultPluginContext: PluginContext = {
    query: async () => {
        console.warn('PluginContext not initialized');
        return [];
    },
    notifyWidget: () => {
        console.warn('PluginContext not initialized');
    },
    subscribeToWidget: () => {
        console.warn('PluginContext not initialized');
        return () => {};
    }
};

// Create the React context
const PluginContextObject = createContext<PluginContext>(defaultPluginContext);

// Provider component
interface PluginContextProviderProps {
    children: ReactNode;
    overrides?: Partial<PluginContext>;
}

export const PluginContextProvider: React.FC<PluginContextProviderProps> = ({ children, overrides }) => {
    // Store widget event subscribers
    const [subscribers, setSubscribers] = useState<Map<string, Set<(event: string, data?: any) => void>>>(
        new Map()
    );

    // Query implementation
    const query = useCallback(async (query: string, variables?: any) => {
        try {
            const response = await apiRequest(`${appConfig.backendUrl}/api/query`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ query, variables }),
            });

            return response.data ?? [];

        } catch (error) {
            console.error("Error querying:", error);
            return [];
        }
    }, []);

    // Notify a widget with an event
    const notifyWidget = useCallback((widgetId: string, event: string, data?: any) => {
        const widgetSubscribers = subscribers.get(widgetId);
        if (widgetSubscribers) {
            widgetSubscribers.forEach(callback => {
                callback(event, data);
            });
        }
    }, [subscribers]);

    // Subscribe to widget events
    const subscribeToWidget = useCallback((
        widgetId: string,
        callback: (event: string, data?: any) => void
    ) => {
        setSubscribers(prev => {
            const newMap = new Map(prev);
            const widgetSubs = newMap.get(widgetId) || new Set();
            widgetSubs.add(callback);
            newMap.set(widgetId, widgetSubs);
            return newMap;
        });

        // Return unsubscribe function
        return () => {
            setSubscribers(prev => {
                const newMap = new Map(prev);
                const widgetSubs = newMap.get(widgetId);
                if (widgetSubs) {
                    widgetSubs.delete(callback);
                    if (widgetSubs.size === 0) {
                        newMap.delete(widgetId);
                    } else {
                        newMap.set(widgetId, widgetSubs);
                    }
                }
                return newMap;
            });
        };
    }, []);

    // Create the context value
    const contextValue: PluginContext = {
        query,
        notifyWidget,
        subscribeToWidget,
        ...overrides, // Apply any overrides
    };

    return (
        <PluginContextObject.Provider value={contextValue}>
            {children}
        </PluginContextObject.Provider>
    );
};

// Hook for accessing the plugin context
export const usePluginContext = (): PluginContext => {
    const context = useContext(PluginContextObject);

    if (!context) {
        throw new Error('usePluginContext must be used within a PluginContextProvider');
    }

    return context;
};